import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Mail, Send, Save, Loader2, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { emailAPI } from '../../api';
import { toast } from 'sonner';

const EmailSettingsTab = ({ emailConfig, setEmailConfig, emailLogs, setEmailLogs, emailConfigured, setEmailConfigured }) => {
  const [testEmailAddr, setTestEmailAddr] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);

  const handleEmailSave = async () => {
    setSavingEmail(true);
    try {
      await emailAPI.updateConfig(emailConfig);
      setEmailConfigured(true);
      toast.success('Email settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save email settings');
    } finally { setSavingEmail(false); }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddr) { toast.error('Please enter a test email address'); return; }
    setSendingTest(true);
    try {
      const res = await emailAPI.testEmail({ to_email: testEmailAddr });
      toast.success(res.data.message || 'Test email sent!');
      const logsRes = await emailAPI.getLogs();
      setEmailLogs(logsRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally { setSendingTest(false); }
  };

  return (
    <>
      <Card className="border-[#E2E6EA]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-[#00A3A3]" />Email Configuration</CardTitle>
          <CardDescription>Configure SMTP email settings so LexAgenda can send meeting notices and communications on your behalf.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-[#F8F9FA] rounded-lg">
            <div>
              <h4 className="font-medium text-[#1A1D21]">Enable Email Sending</h4>
              <p className="text-sm text-[#5A6570]">When enabled, notices and communications will be sent via your configured email</p>
            </div>
            <Switch checked={emailConfig.enabled} onCheckedChange={(checked) => setEmailConfig({ ...emailConfig, enabled: checked })} data-testid="email-enabled-switch" />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="smtp-host">SMTP Server Host</Label>
              <Input id="smtp-host" value={emailConfig.smtp_host} onChange={(e) => setEmailConfig({ ...emailConfig, smtp_host: e.target.value })} placeholder="smtp.gmail.com" className="border-[#E2E6EA]" data-testid="smtp-host-input" />
              <p className="text-xs text-[#5A6570]">e.g., smtp.gmail.com, smtp.office365.com</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp-port">SMTP Port</Label>
              <Input id="smtp-port" type="number" value={emailConfig.smtp_port} onChange={(e) => setEmailConfig({ ...emailConfig, smtp_port: parseInt(e.target.value) || 587 })} className="border-[#E2E6EA]" data-testid="smtp-port-input" />
              <p className="text-xs text-[#5A6570]">587 (STARTTLS) or 465 (SSL/TLS)</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="smtp-email">Email Address</Label>
              <Input id="smtp-email" type="email" value={emailConfig.smtp_email} onChange={(e) => setEmailConfig({ ...emailConfig, smtp_email: e.target.value })} placeholder="secretary@company.com" className="border-[#E2E6EA]" data-testid="smtp-email-input" />
              <p className="text-xs text-[#5A6570]">The email address used to send communications</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp-password">Email Password / App Password</Label>
              <Input id="smtp-password" type="password" value={emailConfig.smtp_password} onChange={(e) => setEmailConfig({ ...emailConfig, smtp_password: e.target.value })} placeholder="App-specific password" className="border-[#E2E6EA]" data-testid="smtp-password-input" />
              <p className="text-xs text-[#5A6570]">For Gmail, use an App Password (not your main password)</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="display-name">Display Name</Label>
              <Input id="display-name" value={emailConfig.display_name} onChange={(e) => setEmailConfig({ ...emailConfig, display_name: e.target.value })} placeholder="Corporate Secretary - ABC Corp" className="border-[#E2E6EA]" data-testid="smtp-display-name-input" />
              <p className="text-xs text-[#5A6570]">Name shown in recipient's inbox (e.g., "Corporate Secretary")</p>
            </div>
            <div className="flex items-end pb-2">
              <div className="flex items-center gap-3 p-3 bg-[#F8F9FA] rounded-lg w-full">
                <Switch checked={emailConfig.use_tls} onCheckedChange={(checked) => setEmailConfig({ ...emailConfig, use_tls: checked })} data-testid="smtp-tls-switch" />
                <div>
                  <p className="text-sm font-medium text-[#1A1D21]">Use TLS/SSL</p>
                  <p className="text-xs text-[#5A6570]">Required for most providers</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleEmailSave} disabled={savingEmail || !emailConfig.smtp_host || !emailConfig.smtp_email} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="save-email-config-btn">
              {savingEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Email Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Test Email */}
      {emailConfigured && (
        <Card className="border-[#E2E6EA]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-[#F58A26]" />Test Email</CardTitle>
            <CardDescription>Send a test email to verify your SMTP configuration is working</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 items-end">
              <div className="flex-1 space-y-2">
                <Label htmlFor="test-email">Recipient Email</Label>
                <Input id="test-email" type="email" value={testEmailAddr} onChange={(e) => setTestEmailAddr(e.target.value)} placeholder="your-test@email.com" className="border-[#E2E6EA]" data-testid="test-email-input" />
              </div>
              <Button onClick={handleTestEmail} disabled={sendingTest || !testEmailAddr} className="bg-[#F58A26] hover:bg-[#E07918] text-white" data-testid="send-test-email-btn">
                {sendingTest ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send Test
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Logs */}
      {emailLogs.length > 0 && (
        <Card className="border-[#E2E6EA]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Email History</CardTitle>
                <CardDescription>Recent emails sent from this organization</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={async () => { const res = await emailAPI.getLogs(); setEmailLogs(res.data || []); }} data-testid="refresh-email-logs-btn">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
            <table className="w-full min-w-[500px]">
              <thead>
                <tr className="border-b border-[#E2E6EA] bg-[#F8F9FA]">
                  <th className="text-left p-3 text-xs font-medium text-[#5A6570]">Date</th>
                  <th className="text-left p-3 text-xs font-medium text-[#5A6570]">To</th>
                  <th className="text-left p-3 text-xs font-medium text-[#5A6570]">Subject</th>
                  <th className="text-left p-3 text-xs font-medium text-[#5A6570]">Type</th>
                  <th className="text-left p-3 text-xs font-medium text-[#5A6570]">Status</th>
                </tr>
              </thead>
              <tbody>
                {emailLogs.map((log) => (
                  <tr key={log.id} className="border-b border-[#E2E6EA] last:border-0">
                    <td className="p-3 text-sm text-[#5A6570]">{new Date(log.sent_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                    <td className="p-3 text-sm text-[#1A1D21]">{(log.to_emails || []).join(', ')}</td>
                    <td className="p-3 text-sm text-[#1A1D21] max-w-[200px] truncate">{log.subject}</td>
                    <td className="p-3"><Badge variant="outline" className="text-[10px] capitalize">{log.type}</Badge></td>
                    <td className="p-3">
                      {log.status === 'sent' ? (
                        <span className="flex items-center gap-1 text-xs text-green-600"><CheckCircle className="h-3.5 w-3.5" /> Sent</span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-red-600"><XCircle className="h-3.5 w-3.5" /> Failed</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* SMTP Help */}
      <Card className="border-[#00A3A3]/20 bg-[#00A3A3]/5">
        <CardContent className="py-4 px-5">
          <h4 className="font-medium text-[#1A1D21] mb-2">SMTP Setup Guide</h4>
          <ul className="text-xs text-[#5A6570] space-y-1.5">
            <li><strong>Gmail:</strong> Use smtp.gmail.com, port 587. Generate an App Password at myaccount.google.com &gt; Security &gt; 2-Step Verification &gt; App Passwords.</li>
            <li><strong>Outlook/Office 365:</strong> Use smtp.office365.com, port 587.</li>
            <li><strong>Custom SMTP:</strong> Contact your IT administrator for the SMTP host, port, and credentials.</li>
            <li><strong>Security:</strong> Your password is stored securely and never displayed after saving. All emails are sent over encrypted connections.</li>
          </ul>
        </CardContent>
      </Card>
    </>
  );
};

export default EmailSettingsTab;
